import CSS from "csstype";
import React, { useCallback, useEffect, useState } from "react";
import { userAddBot } from "~src/user/apigen/addBot";
import { userCreateTurn, UserCreateTurnInvalid } from "~src/user/apigen/createTurn";
import { IUserGetStateReturn } from "~src/user/apigen/getState";
import { userPlayAgain } from "~src/user/apigen/playAgain";
import { userTakeMiddleCards } from "~src/user/apigen/takeMiddleCards";
import { AddBot } from "./AddBot";
import { getLatestTurnDisplay } from "./turnDisplay";

const maxRoomUserCount = 3;

type OptionsProps = {
  state: IUserGetStateReturn;
  requestPending: boolean;
  applyPendingTurn: (selectedCards: number[]) => void;
  applyPendingTakeMiddleCards: () => void;
  applyPendingPlayAgain: () => void;
  revalidateState: () => Promise<void>;
  revertOptimisticState: () => Promise<void>;
  setSelectedCards: (cards: number[]) => void;
  selectedCards: number[];
};

export const Options: React.FC<OptionsProps> = (
  {
    state,
    requestPending,
    applyPendingTurn,
    applyPendingTakeMiddleCards,
    applyPendingPlayAgain,
    revalidateState,
    revertOptimisticState,
    selectedCards,
    setSelectedCards,
  },
) => {
  const [pendingBotAdds, setPendingBotAdds] = useState<number>(0);

  useEffect(() => {
    if (state.hands.length !== 0) {
      setPendingBotAdds(0);
      return;
    }

    const openSlots = Math.max(0, maxRoomUserCount - state.userCount);
    setPendingBotAdds(currentPending => Math.min(currentPending, openSlots));
  }, [state.hands.length, state.userCount]);

  const play = useCallback(async () => {
    if (selectedCards.length === 0) {
      return;
    }

    const nextSelectedCards = [...selectedCards];
    applyPendingTurn(nextSelectedCards);
    setSelectedCards([]);

    try {
      await userCreateTurn({ selectedCards: nextSelectedCards });
    } catch (e) {
      setSelectedCards(nextSelectedCards);
      await revertOptimisticState();
      if (!(e instanceof UserCreateTurnInvalid)) {
        alert(e);
      }
      return;
    }

    void revalidateState();
  }, [
    applyPendingTurn,
    revertOptimisticState,
    revalidateState,
    selectedCards,
    setSelectedCards,
  ]);

  const pass = useCallback(async () => {
    applyPendingTurn([]);

    try {
      await userCreateTurn({ selectedCards: [] });
    } catch (e) {
      await revertOptimisticState();
      if (!(e instanceof UserCreateTurnInvalid)) {
        alert(e);
      }
      return;
    }

    setSelectedCards([]);
    void revalidateState();
  }, [applyPendingTurn, revertOptimisticState, revalidateState, setSelectedCards]);

  const takeMiddleCards = useCallback(async () => {
    applyPendingTakeMiddleCards();

    try {
      await userTakeMiddleCards();
    } catch (e) {
      await revertOptimisticState();
      alert(e);
      return;
    }

    void revalidateState();
  }, [applyPendingTakeMiddleCards, revalidateState, revertOptimisticState]);

  const playAgain = useCallback(async () => {
    applyPendingPlayAgain();

    try {
      await userPlayAgain();
    } catch (e) {
      await revertOptimisticState();
      alert(e);
      return;
    }

    void revalidateState();
  }, [applyPendingPlayAgain, revalidateState, revertOptimisticState]);

  const resetHand = useCallback(() => setSelectedCards([]), [setSelectedCards]);
  const displayedUserCount = Math.min(state.userCount + pendingBotAdds, maxRoomUserCount);
  const addBot = useCallback(async () => {
    if (state.hands.length !== 0) {
      return;
    }

    let shouldRequest = false;

    setPendingBotAdds(currentPending => {
      if (state.userCount + currentPending >= maxRoomUserCount) {
        return currentPending;
      }

      shouldRequest = true;
      return currentPending + 1;
    });

    if (!shouldRequest) {
      return;
    }

    try {
      await userAddBot();
    } catch (e) {
      setPendingBotAdds(currentPending => Math.max(0, currentPending - 1));
      alert(e);
      return;
    }

    void revalidateState();
  }, [revalidateState, state.hands.length, state.userCount]);

  if (state.hands.length === 0) {
    return (
      <div style={waiting}>
        <div>
          waiting {displayedUserCount}/{maxRoomUserCount}
        </div>
        <AddBot disabled={displayedUserCount >= maxRoomUserCount} addBot={addBot} />
      </div>
    );
  }

  if (state.results.length !== 0) {
    if (!state.playAgain) {
      return (
        <div style={playStyle}>
          <span onClick={playAgain}>play again</span>
        </div>
      );
    } else {
      return null;
    }
  }

  if (state.observer) {
    return null;
  }

  const latestTurn = getLatestTurnDisplay(state);
  if (latestTurn?.actor === "bottom") {
    return (
      <div style={playStyle}>
        <span>{latestTurn.action === "pass" ? "passed" : "\u2191"}</span>
      </div>
    );
  }

  if (state.position !== state.turn) {
    return null;
  }

  if (requestPending) {
    return null;
  }

  if (state.landlordHandID === null) {
    return (
      <div style={playStyle}>
        <span onClick={pass}>pass</span>
        <span style={tab}></span>
        <span onClick={takeMiddleCards}>take</span>
      </div>
    );
  }

  return (
    <div style={playStyle}>
      {selectedCards.length === 0
        ? <span onClick={pass}>pass</span>
        : <span onClick={resetHand}>reset</span>}
      <span style={tab}></span>
      <span onClick={play}>play</span>
    </div>
  );
};

const tab: CSS.Properties = {
  width: "20px",
};

const playStyle: CSS.Properties = {
  position: "absolute",
  bottom: "25%",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "1.5em",
  cursor: "pointer",
};

const waiting: CSS.Properties = {
  position: "absolute",
  bottom: "25%",
  left: "50%",
  transform: "translateX(-50%)",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "1.5em",
  cursor: "pointer",
};
